import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useEffect, useState, useMemo } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { useToast } from '@ais-ui/ui-core';
import { useSaveSettings } from '@ais-ui/websockets';
import { useSettings } from '../SettingsProvider';
import { useSettingsPage } from '../SettingsPageProvider';

// Combined schema for all map settings
const mapFormSchema = z.object({
  // Projection setting
  displayProjection: z.string(),

  // Track settings
  enable_track: z.boolean(),
  show_samples: z.boolean(),
  show_direction_arrows: z.boolean(),
  interval_in_minutes: z.number().int(),
  history_in_hours: z.number().int(),
  track_max_position_jump_in_meters: z.number(),
  track_max_position_jump_in_knots: z.number(),

  // Label settings
  labelZoomLevel: z.number(),
  labelZoomLevelFilter: z.number(),
  name: z.boolean(),
  mmsi: z.boolean(),
  callSign: z.boolean(),
  imo: z.boolean(),
  typeOfShip: z.boolean(),
  typeOfCargo: z.boolean(),
  country: z.boolean(),
  destination: z.boolean(),
  size: z.boolean(),
  speedOverGround: z.boolean(),
  courseOverGround: z.boolean(),
  heading: z.boolean(),
  draught: z.boolean(),
  navigationalStatus: z.boolean(),
  aisType: z.boolean(),

  // Vessel hover label settings
  vessel_mmsi: z.boolean(),
  vessel_callSign: z.boolean(),
  vessel_imo: z.boolean(),
  vessel_typeOfShip: z.boolean(),
  vessel_typeOfCargo: z.boolean(),
  vessel_country: z.boolean(),
  vessel_destination: z.boolean(),
  vessel_size: z.boolean(),
  vessel_speedOverGround: z.boolean(),
  vessel_courseOverGround: z.boolean(),
  vessel_heading: z.boolean(),
  vessel_draught: z.boolean(),
  vessel_navigationalStatus: z.boolean(),
  vessel_aisType: z.boolean(),

  // ATON hover label settings
  aton_assignedMode: z.boolean(),
  aton_atonType: z.boolean(),
  aton_country: z.boolean(),
  aton_latitude: z.boolean(),
  aton_longitude: z.boolean(),
  aton_length: z.boolean(),
  aton_mmsi: z.boolean(),
  aton_offPosition: z.boolean(),
  aton_state: z.boolean(),
  aton_status: z.boolean(),
  aton_targetType: z.boolean(),
  aton_width: z.boolean(),

  // BaseStation hover label settings
  basestation_country: z.boolean(),
  basestation_latitude: z.boolean(),
  basestation_longitude: z.boolean(),
  basestation_mmsi: z.boolean(),
  basestation_vdlLoad: z.boolean(),

  // Virtual ATON hover label settings
  virtualaton_assignedMode: z.boolean(),
  virtualaton_virtualAtonType: z.boolean(),
  virtualaton_country: z.boolean(),
  virtualaton_latitude: z.boolean(),
  virtualaton_longitude: z.boolean(),
  virtualaton_length: z.boolean(),
  virtualaton_mmsi: z.boolean(),
  virtualaton_offPosition: z.boolean(),
  virtualaton_state: z.boolean(),
  virtualaton_status: z.boolean(),
  virtualaton_virtualAtonTargetType: z.boolean(),
  virtualaton_width: z.boolean(),

  // Met/Hyd hover label settings
  methyd_airRelatedSettings: z.boolean(),
  methyd_waterRelatedSettings: z.boolean(),
  methyd_lastOrigin: z.boolean(),
  methyd_location: z.boolean(),
  methyd_message: z.boolean(),
  methyd_metHydLatitude: z.boolean(),
  methyd_metHydLongitude: z.boolean(),
  methyd_latitude: z.boolean(),
  methyd_longitude: z.boolean(),
  methyd_metHydMmsi: z.boolean(),
  methyd_metHydTimestamp: z.boolean(),
  methyd_reportTimestamp: z.boolean(),
  methyd_siteId: z.boolean(),
  methyd_stationName: z.boolean(),
  methyd_dataTimeout: z.boolean(),
  methyd_owner: z.boolean(),

  // Radar hover label settings
  radar_id: z.boolean(),
  radar_name: z.boolean(),
  radar_sog: z.boolean(),
  radar_cog: z.boolean(),
  radar_lastRadarReport: z.boolean(),
  radar_lastOrigin: z.boolean(),
  radar_latitude: z.boolean(),
  radar_longitude: z.boolean(),

  // DAS hover label settings
  das_trackId: z.boolean(),
  das_site: z.boolean(),
  das_priority: z.boolean(),
  das_lastReportTimestamp: z.boolean(),
  das_latitude: z.boolean(),
  das_longitude: z.boolean(),
  das_speed: z.boolean(),
  das_heading: z.boolean(),
  das_accuracy: z.boolean(),
  das_lastOrigin: z.boolean(),

  // Drone hover label settings
  drone_uasId: z.boolean(),
  drone_macAddress: z.boolean(),
  drone_uaType: z.boolean(),
  drone_altitude: z.boolean(),
  drone_speed: z.boolean(),
  drone_heading: z.boolean(),
  drone_latitude: z.boolean(),
  drone_longitude: z.boolean(),
  drone_operatorLatitude: z.boolean(),
  drone_operatorLongitude: z.boolean(),
  drone_lastReportTimestamp: z.boolean(),
  drone_operationStatus: z.boolean(),
});

export type MapFormValues = z.infer<typeof mapFormSchema>;

export const useMapForm = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { mutate: mutateSettings } = useSaveSettings();
  const { setUnsavedSettings } = useSettingsPage();

  const initialValues: MapFormValues = useMemo(
    () => ({
      // Projection setting
      displayProjection:
        settings['ribbonPlugin.settings']?.display_projection ?? 'EPSG:3857',

      // Track settings
      enable_track: settings['ribbonPlugin.settings'].enable_history,
      history_in_hours: settings['ribbonPlugin.settings'].track_lookback_time,
      interval_in_minutes:
        settings['ribbonPlugin.settings'].track_timestamp_interval,
      show_samples: settings['ribbonPlugin.settings'].track_show_samples,
      show_direction_arrows: settings['ribbonPlugin.settings'].track_show_dir,
      track_max_position_jump_in_meters:
        settings['ribbonPlugin.settings'].track_max_pos_jump,
      track_max_position_jump_in_knots:
        settings['ribbonPlugin.settings'].track_max_speed,

      // Label settings
      labelZoomLevel:
        settings['ribbonPlugin.settings'].label_zoom_level_nofilter ?? 9,
      labelZoomLevelFilter:
        settings['ribbonPlugin.settings'].label_zoom_level_filter ?? 9,
      name: settings['ribbonPlugin.settings'].show_label_name ?? false,
      mmsi: settings['ribbonPlugin.settings'].show_label_mmsi ?? false,
      aisType: settings['ribbonPlugin.settings'].show_label_ais_type ?? false,
      callSign: settings['ribbonPlugin.settings'].show_label_callsign ?? false,
      imo: settings['ribbonPlugin.settings'].show_label_imo ?? false,
      typeOfShip:
        settings['ribbonPlugin.settings'].show_label_shiptype ?? false,
      typeOfCargo:
        settings['ribbonPlugin.settings'].show_label_cargotype ?? false,
      country: settings['ribbonPlugin.settings'].show_label_country ?? false,
      destination:
        settings['ribbonPlugin.settings'].show_label_destination ?? false,
      size: settings['ribbonPlugin.settings'].show_label_size ?? false,
      speedOverGround:
        settings['ribbonPlugin.settings'].show_label_sog ?? false,
      courseOverGround:
        settings['ribbonPlugin.settings'].show_label_cog ?? false,
      heading: settings['ribbonPlugin.settings'].show_label_heading ?? false,
      draught: settings['ribbonPlugin.settings'].show_label_draught ?? false,
      navigationalStatus:
        settings['ribbonPlugin.settings'].show_label_navstatus ?? false,

      // Vessel hover label settings
      vessel_mmsi: settings['ribbonPlugin.settings']?.show_vessel_mmsi ?? false,
      vessel_aisType:
        settings['ribbonPlugin.settings']?.show_vessel_ais_type ?? false,
      vessel_callSign:
        settings['ribbonPlugin.settings']?.show_vessel_callsign ?? false,
      vessel_imo: settings['ribbonPlugin.settings']?.show_vessel_imo ?? false,
      vessel_typeOfShip:
        settings['ribbonPlugin.settings']?.show_vessel_shiptype ?? false,
      vessel_typeOfCargo:
        settings['ribbonPlugin.settings']?.show_vessel_cargotype ?? false,
      vessel_country:
        settings['ribbonPlugin.settings']?.show_vessel_country ?? false,
      vessel_destination:
        settings['ribbonPlugin.settings']?.show_vessel_destination ?? false,
      vessel_size: settings['ribbonPlugin.settings']?.show_vessel_size ?? false,
      vessel_speedOverGround:
        settings['ribbonPlugin.settings']?.show_vessel_sog ?? false,
      vessel_courseOverGround:
        settings['ribbonPlugin.settings']?.show_vessel_cog ?? false,
      vessel_heading:
        settings['ribbonPlugin.settings']?.show_vessel_heading ?? false,
      vessel_draught:
        settings['ribbonPlugin.settings']?.show_vessel_draught ?? false,
      vessel_navigationalStatus:
        settings['ribbonPlugin.settings']?.show_vessel_navstatus ?? false,

      // ATON hover label settings
      aton_assignedMode:
        settings['ribbonPlugin.settings']?.show_aton_assigned_mode ?? false,
      aton_atonType: settings['ribbonPlugin.settings']?.show_aton_type ?? false,
      aton_country:
        settings['ribbonPlugin.settings']?.show_aton_country ?? false,
      aton_latitude:
        settings['ribbonPlugin.settings']?.show_aton_latitude ?? false,
      aton_longitude:
        settings['ribbonPlugin.settings']?.show_aton_longitude ?? false,
      aton_length: settings['ribbonPlugin.settings']?.show_aton_length ?? false,
      aton_mmsi: settings['ribbonPlugin.settings']?.show_aton_mmsi ?? false,
      aton_offPosition:
        settings['ribbonPlugin.settings']?.show_aton_off_position ?? false,
      aton_state: settings['ribbonPlugin.settings']?.show_aton_state ?? false,
      aton_status: settings['ribbonPlugin.settings']?.show_aton_status ?? false,
      aton_targetType:
        settings['ribbonPlugin.settings']?.show_aton_target_type ?? false,
      aton_width: settings['ribbonPlugin.settings']?.show_aton_width ?? false,

      // BaseStation hover label settings
      basestation_country:
        settings['ribbonPlugin.settings']?.show_basestation_country ?? false,
      basestation_latitude:
        settings['ribbonPlugin.settings']?.show_basestation_latitude ?? false,
      basestation_longitude:
        settings['ribbonPlugin.settings']?.show_basestation_longitude ?? false,
      basestation_mmsi:
        settings['ribbonPlugin.settings']?.show_basestation_mmsi ?? false,
      basestation_vdlLoad:
        settings['ribbonPlugin.settings']?.show_basestation_vdl_load ?? false,

      // Virtual ATON hover label settings
      virtualaton_assignedMode:
        settings['ribbonPlugin.settings']?.show_virtual_aton_assigned_mode ??
        false,
      virtualaton_virtualAtonType:
        settings['ribbonPlugin.settings']?.show_virtual_aton_type ?? false,
      virtualaton_country:
        settings['ribbonPlugin.settings']?.show_virtual_aton_country ?? false,
      virtualaton_latitude:
        settings['ribbonPlugin.settings']?.show_virtual_aton_latitude ?? false,
      virtualaton_longitude:
        settings['ribbonPlugin.settings']?.show_virtual_aton_longitude ?? false,
      virtualaton_length:
        settings['ribbonPlugin.settings']?.show_virtual_aton_length ?? false,
      virtualaton_mmsi:
        settings['ribbonPlugin.settings']?.show_virtual_aton_mmsi ?? false,
      virtualaton_offPosition:
        settings['ribbonPlugin.settings']?.show_virtual_aton_off_position ??
        false,
      virtualaton_state:
        settings['ribbonPlugin.settings']?.show_virtual_aton_state ?? false,
      virtualaton_status:
        settings['ribbonPlugin.settings']?.show_virtual_aton_status ?? false,
      virtualaton_virtualAtonTargetType:
        settings['ribbonPlugin.settings']?.show_virtual_aton_target_type ??
        false,
      virtualaton_width:
        settings['ribbonPlugin.settings']?.show_virtual_aton_width ?? false,

      // Met/Hyd hover label settings
      methyd_airRelatedSettings:
        settings['ribbonPlugin.settings']?.show_methyd_air_related ?? false,
      methyd_waterRelatedSettings:
        settings['ribbonPlugin.settings']?.show_methyd_water_related ?? false,
      methyd_lastOrigin:
        settings['ribbonPlugin.settings']?.show_methyd_last_origin ?? false,
      methyd_location:
        settings['ribbonPlugin.settings']?.show_methyd_location ?? false,
      methyd_message:
        settings['ribbonPlugin.settings']?.show_methyd_message ?? false,
      methyd_metHydLatitude:
        settings['ribbonPlugin.settings']?.show_methyd_latitude ?? false,
      methyd_metHydLongitude:
        settings['ribbonPlugin.settings']?.show_methyd_longitude ?? false,
      methyd_latitude:
        settings['ribbonPlugin.settings']?.show_mathyd_latitude_degree ?? false,
      methyd_longitude:
        settings['ribbonPlugin.settings']?.show_methyd_longitude_degree ??
        false,
      methyd_metHydMmsi:
        settings['ribbonPlugin.settings']?.show_methyd_mmsi ?? false,
      methyd_metHydTimestamp:
        settings['ribbonPlugin.settings']?.show_methyd_timestamp ?? false,
      methyd_reportTimestamp:
        settings['ribbonPlugin.settings']?.show_methyd_report_timestamp ??
        false,
      methyd_siteId:
        settings['ribbonPlugin.settings']?.show_methyd_site_id ?? false,
      methyd_stationName:
        settings['ribbonPlugin.settings']?.show_methyd_station_name ?? false,
      methyd_dataTimeout:
        settings['ribbonPlugin.settings']?.show_methyd_data_timeout ?? false,
      methyd_owner:
        settings['ribbonPlugin.settings']?.show_methyd_owner ?? false,

      // Radar hover label settings
      radar_id: settings['ribbonPlugin.settings']?.show_radar_id ?? false,
      radar_name: settings['ribbonPlugin.settings']?.show_radar_name ?? false,
      radar_sog: settings['ribbonPlugin.settings']?.show_radar_sog ?? false,
      radar_cog: settings['ribbonPlugin.settings']?.show_radar_cog ?? false,
      radar_lastRadarReport:
        settings['ribbonPlugin.settings']?.show_radar_last_report ?? false,
      radar_lastOrigin:
        settings['ribbonPlugin.settings']?.show_radar_last_origin ?? false,
      radar_latitude:
        settings['ribbonPlugin.settings']?.show_radar_latitude ?? false,
      radar_longitude:
        settings['ribbonPlugin.settings']?.show_radar_longitude ?? false,

      // DAS hover label settings
      das_trackId:
        settings['ribbonPlugin.settings']?.show_das_track_id ?? false,
      das_site: settings['ribbonPlugin.settings']?.show_das_site ?? false,
      das_priority:
        settings['ribbonPlugin.settings']?.show_das_priority ?? false,
      das_lastReportTimestamp:
        settings['ribbonPlugin.settings']?.show_das_last_report ?? false,
      das_latitude:
        settings['ribbonPlugin.settings']?.show_das_latitude ?? false,
      das_longitude:
        settings['ribbonPlugin.settings']?.show_das_longitude ?? false,
      das_speed: settings['ribbonPlugin.settings']?.show_das_speed ?? false,
      das_heading: settings['ribbonPlugin.settings']?.show_das_heading ?? false,
      das_accuracy:
        settings['ribbonPlugin.settings']?.show_das_accuracy ?? false,
      das_lastOrigin:
        settings['ribbonPlugin.settings']?.show_das_last_origin ?? false,

      // Drone hover label settings
      drone_uasId: settings['ribbonPlugin.settings']?.show_drone_uas_id ?? false,
      drone_macAddress:
        settings['ribbonPlugin.settings']?.show_drone_mac_address ?? false,
      drone_uaType:
        settings['ribbonPlugin.settings']?.show_drone_ua_type ?? false,
      drone_altitude:
        settings['ribbonPlugin.settings']?.show_drone_altitude ?? false,
      drone_speed: settings['ribbonPlugin.settings']?.show_drone_speed ?? false,
      drone_heading:
        settings['ribbonPlugin.settings']?.show_drone_heading ?? false,
      drone_latitude:
        settings['ribbonPlugin.settings']?.show_drone_latitude ?? false,
      drone_longitude:
        settings['ribbonPlugin.settings']?.show_drone_longitude ?? false,
      drone_operatorLatitude:
        settings['ribbonPlugin.settings']?.show_drone_operator_latitude ?? false,
      drone_operatorLongitude:
        settings['ribbonPlugin.settings']?.show_drone_operator_longitude ?? false,
      drone_lastReportTimestamp:
        settings['ribbonPlugin.settings']?.show_drone_last_report ?? false,
      drone_operationStatus:
        settings['ribbonPlugin.settings']?.show_drone_operation_status ?? false,
    }),
    [settings]
  );

  const form = useForm<MapFormValues>({
    resolver: zodResolver(mapFormSchema),
    defaultValues: initialValues,
  });

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  const onSubmit = (data: MapFormValues) => {
    setIsSubmitting(true);

    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        // Projection setting
        display_projection: data.displayProjection,

        // Track settings
        enable_history: data.enable_track,
        track_show_samples: data.show_samples,
        track_timestamp_interval: data.interval_in_minutes,
        track_lookback_time: data.history_in_hours,
        track_show_dir: data.show_direction_arrows,
        track_max_pos_jump: data.track_max_position_jump_in_meters,
        track_max_speed: data.track_max_position_jump_in_knots,

        // Label settings
        label_zoom_level_nofilter: data.labelZoomLevel,
        label_zoom_level_filter: data.labelZoomLevelFilter,
        show_label_name: data.name,
        show_label_mmsi: data.mmsi,
        show_label_callsign: data.callSign,
        show_label_imo: data.imo,
        show_label_shiptype: data.typeOfShip,
        show_label_cargotype: data.typeOfCargo,
        show_label_country: data.country,
        show_label_destination: data.destination,
        show_label_size: data.size,
        show_label_sog: data.speedOverGround,
        show_label_cog: data.courseOverGround,
        show_label_heading: data.heading,
        show_label_draught: data.draught,
        show_label_navstatus: data.navigationalStatus,
        show_label_ais_type: data.aisType,

        // Vessel hover label settings
        show_vessel_mmsi: data.vessel_mmsi,
        show_vessel_callsign: data.vessel_callSign,
        show_vessel_imo: data.vessel_imo,
        show_vessel_shiptype: data.vessel_typeOfShip,
        show_vessel_cargotype: data.vessel_typeOfCargo,
        show_vessel_country: data.vessel_country,
        show_vessel_destination: data.vessel_destination,
        show_vessel_size: data.vessel_size,
        show_vessel_sog: data.vessel_speedOverGround,
        show_vessel_cog: data.vessel_courseOverGround,
        show_vessel_heading: data.vessel_heading,
        show_vessel_draught: data.vessel_draught,
        show_vessel_navstatus: data.vessel_navigationalStatus,
        show_vessel_ais_type: data.vessel_aisType,

        // ATON hover label settings
        show_aton_assigned_mode: data.aton_assignedMode,
        show_aton_type: data.aton_atonType,
        show_aton_country: data.aton_country,
        show_aton_latitude: data.aton_latitude,
        show_aton_longitude: data.aton_longitude,
        show_aton_length: data.aton_length,
        show_aton_mmsi: data.aton_mmsi,
        show_aton_off_position: data.aton_offPosition,
        show_aton_state: data.aton_state,
        show_aton_status: data.aton_status,
        show_aton_target_type: data.aton_targetType,
        show_aton_width: data.aton_width,

        // BaseStation hover label settings
        show_basestation_country: data.basestation_country,
        show_basestation_latitude: data.basestation_latitude,
        show_basestation_longitude: data.basestation_longitude,
        show_basestation_mmsi: data.basestation_mmsi,
        show_basestation_vdl_load: data.basestation_vdlLoad,

        // Virtual ATON hover label settings
        show_virtual_aton_assigned_mode: data.virtualaton_assignedMode,
        show_virtual_aton_type: data.virtualaton_virtualAtonType,
        show_virtual_aton_country: data.virtualaton_country,
        show_virtual_aton_latitude: data.virtualaton_latitude,
        show_virtual_aton_longitude: data.virtualaton_longitude,
        show_virtual_aton_length: data.virtualaton_length,
        show_virtual_aton_mmsi: data.virtualaton_mmsi,
        show_virtual_aton_off_position: data.virtualaton_offPosition,
        show_virtual_aton_state: data.virtualaton_state,
        show_virtual_aton_status: data.virtualaton_status,
        show_virtual_aton_target_type: data.virtualaton_virtualAtonTargetType,
        show_virtual_aton_width: data.virtualaton_width,

        // Met/Hyd hover label settings
        show_methyd_air_related: data.methyd_airRelatedSettings,
        show_methyd_water_related: data.methyd_waterRelatedSettings,
        show_methyd_last_origin: data.methyd_lastOrigin,
        show_methyd_location: data.methyd_location,
        show_methyd_message: data.methyd_message,
        show_methyd_latitude: data.methyd_metHydLatitude,
        show_methyd_longitude: data.methyd_metHydLongitude,
        show_mathyd_latitude_degree: data.methyd_latitude,
        show_methyd_longitude_degree: data.methyd_longitude,
        show_methyd_mmsi: data.methyd_metHydMmsi,
        show_methyd_timestamp: data.methyd_metHydTimestamp,
        show_methyd_report_timestamp: data.methyd_reportTimestamp,
        show_methyd_site_id: data.methyd_siteId,
        show_methyd_station_name: data.methyd_stationName,
        show_methyd_data_timeout: data.methyd_dataTimeout,
        show_methyd_owner: data.methyd_owner,

        // Radar hover label settings
        show_radar_id: data.radar_id,
        show_radar_name: data.radar_name,
        show_radar_sog: data.radar_sog,
        show_radar_cog: data.radar_cog,
        show_radar_last_report: data.radar_lastRadarReport,
        show_radar_last_origin: data.radar_lastOrigin,
        show_radar_latitude: data.radar_latitude,
        show_radar_longitude: data.radar_longitude,

        // DAS hover label settings
        show_das_track_id: data.das_trackId,
        show_das_site: data.das_site,
        show_das_priority: data.das_priority,
        show_das_last_report: data.das_lastReportTimestamp,
        show_das_latitude: data.das_latitude,
        show_das_longitude: data.das_longitude,
        show_das_speed: data.das_speed,
        show_das_heading: data.das_heading,
        show_das_accuracy: data.das_accuracy,
        show_das_last_origin: data.das_lastOrigin,

        // Drone hover label settings
        show_drone_uas_id: data.drone_uasId,
        show_drone_mac_address: data.drone_macAddress,
        show_drone_ua_type: data.drone_uaType,
        show_drone_altitude: data.drone_altitude,
        show_drone_speed: data.drone_speed,
        show_drone_heading: data.drone_heading,
        show_drone_latitude: data.drone_latitude,
        show_drone_longitude: data.drone_longitude,
        show_drone_operator_latitude: data.drone_operatorLatitude,
        show_drone_operator_longitude: data.drone_operatorLongitude,
        show_drone_last_report: data.drone_lastReportTimestamp,
        show_drone_operation_status: data.drone_operationStatus,
      },
    };

    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-map-settings-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsMapSuccessUpdateMessage,
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          className: 'update-map-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsMapFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  };

  return {
    form,
    onSubmit,
    isSubmitting,
    initialValues,
  };
};
